import React, { useState } from 'react';
import Modal from '@/components/UI/Modal';
import FormSelect from '@/components/UI/FormSelect.tsx';
import FormInput from '@/components/UI/FormInput.tsx';
import { Button } from '@/components/UI';
import { trpc } from '@/api/trpc';
import { useTrpcMutation } from '@/api/useTrpcMutation';

interface CreatePortfolioModalProps {
    show: boolean;
    onHide: () => void;
    onSuccess: () => void;
}

const CreatePortfolioModal: React.FC<CreatePortfolioModalProps> = ({ show, onHide, onSuccess }) => {
    // State
    const [portfolioName, setPortfolioName] = useState('');
    const [portfolioType, setPortfolioType] = useState<'paper' | 'live'>('paper');
    const [errors, setErrors] = useState<string[]>([]);
    const [isSubmitting, setIsSubmitting] = useState(false);

    // Handlers
    const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setPortfolioName(e.target.value);
    };

    const handleTypeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        setPortfolioType(e.target.value as 'paper' | 'live');
    };

    const validateForm = (): boolean => {
        const newErrors: string[] = [];

        if (!portfolioName || portfolioName.length < 6 || portfolioName.length > 200) {
            newErrors.push('Please enter a portfolio name between 6 and 200 characters.');
        }

        if (portfolioType !== 'live' && portfolioType !== 'paper') {
            newErrors.push('Invalid portfolio type.');
        }

        setErrors(newErrors);
        return newErrors.length === 0;
    };

    // Setup mutation for creating portfolio using the new useTrpcMutation pattern
    const createPortfolioMutation = useTrpcMutation(
        trpc.user.createPortfolio.mutationOptions({
            onSuccess: () => {
                handleSuccess();
            },
            onError: () => {
                setIsSubmitting(false);
            }
        }),
        {
            successMessage: 'Portfolio created successfully',
            fallbackErrorMessage: 'Failed to create portfolio'
        }
    );

    const handleSubmit = () => {
        if (!validateForm()) return;

        setIsSubmitting(true);

        try {
            createPortfolioMutation.mutate({
                name: portfolioName,
                type: portfolioType
            });
        } catch (error: unknown) {
            setErrors([`Failed to create portfolio: ${error instanceof Error ? error.message : 'Unknown error'}`]);
            setIsSubmitting(false);
        }
    };

    const handleSuccess = () => {
        // Reset form
        setPortfolioName('');
        setPortfolioType('paper');
        setErrors([]);
        setIsSubmitting(false);

        // Call onSuccess callback and close modal
        onSuccess();
        onHide();
    };

    const handleClose = () => {
        // Reset form state when closing
        setPortfolioName('');
        setPortfolioType('paper');
        setErrors([]);
        setIsSubmitting(false);
        onHide();
    };

    // Modal footer with action buttons
    const modalFooter = (
        <>
            <Button disabled={isSubmitting} variant="secondary" onClick={handleClose}>
                Cancel
            </Button>
            <Button disabled={isSubmitting} variant="primary" onClick={handleSubmit}>
                {isSubmitting ? (
                    <>
                        <svg
                            className="animate-spin -ml-1 mr-2 h-4 w-4 text-white inline-block"
                            fill="none"
                            viewBox="0 0 24 24"
                            xmlns="http://www.w3.org/2000/svg"
                        >
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                            <path
                                className="opacity-75"
                                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                                fill="currentColor"
                            />
                        </svg>
                        Creating...
                    </>
                ) : (
                    'Create Portfolio'
                )}
            </Button>
        </>
    );

    return (
        <Modal footer={modalFooter} isOpen={show} title="Create Portfolio" onClose={handleClose}>
            {errors.length > 0 && (
                <div className="p-4 mb-4 rounded-md bg-red-100 text-red-800 border border-red-200">
                    {errors.map((error, index) => (
                        <div key={index}>{error}</div>
                    ))}
                </div>
            )}

            <form className="space-y-4">
                <div className="mb-4">
                    <label className="block text-sm font-medium text-zinc-200 mb-1" htmlFor="portfolioName">
                        Portfolio Name
                    </label>
                    <FormInput
                        disabled={isSubmitting}
                        id="portfolioName"
                        placeholder="Enter portfolio name"
                        type="text"
                        value={portfolioName}
                        onChange={handleNameChange}
                    />
                </div>

                <div className="mb-4">
                    <label className="block text-sm font-medium text-zinc-200 mb-1" htmlFor="portfolioType">
                        Portfolio Type
                    </label>
                    <FormSelect
                        disabled={isSubmitting}
                        id="portfolioType"
                        options={[
                            { value: 'paper', label: 'Paper Trading' },
                            { value: 'live', label: 'Live Trading' }
                        ]}
                        value={portfolioType}
                        onChange={handleTypeChange}
                    />
                </div>
            </form>
        </Modal>
    );
};

export default CreatePortfolioModal;
